import { jsx as _jsx } from "react/jsx-runtime";
import { useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useMatch } from 'react-router-dom';
import { EnvironmentsContext } from '../environments';
const LANE_STORAGE_KEY_PREFIX = 'site-app.time-travel-lane';
export const TimeTravelLaneContext = createContext({
    selectedLane: 1,
    setSelectedLane: () => { },
    laneCount: 1,
});
const readStoredLane = (environmentId, laneCount) => {
    if (!environmentId || laneCount <= 1) {
        return 1;
    }
    try {
        const raw = window.sessionStorage.getItem(`${LANE_STORAGE_KEY_PREFIX}.${environmentId}`);
        const parsed = raw ? Number.parseInt(raw, 10) : 1;
        if (Number.isFinite(parsed) && parsed >= 1 && parsed <= laneCount) {
            return parsed;
        }
    }
    catch {
        // sessionStorage may be unavailable
    }
    return 1;
};
/**
 * Owns the active time-travel lane for the current study environment.
 * Multi-lane selection is internal-only (Medable staff), matching study management.
 */
export const TimeTravelLaneProvider = ({ children }) => {
    const match = useMatch('/site/study/:studyId/*');
    const studyId = match?.params?.studyId;
    const { environments } = useContext(EnvironmentsContext);
    const queryClient = useQueryClient();
    const environment = useMemo(() => (studyId ? environments?.find((env) => +env.id === +studyId) : undefined), [environments, studyId]);
    const laneCount = useMemo(() => {
        if (!environment) {
            return 1;
        }
        const lanes = environment.metadata?.lanes;
        return lanes && lanes > 1 ? lanes : 1;
    }, [environment]);
    const [selectedLaneState, setSelectedLaneState] = useState(1);
    useEffect(() => {
        setSelectedLaneState(readStoredLane(environment?.id, laneCount));
    }, [environment?.id, laneCount]);
    const setSelectedLane = useCallback((lane) => {
        const nextLane = laneCount > 1 && lane >= 1 && lane <= laneCount ? lane : 1;
        setSelectedLaneState(nextLane);
        if (environment?.id && laneCount > 1) {
            try {
                window.sessionStorage.setItem(`${LANE_STORAGE_KEY_PREFIX}.${environment.id}`, String(nextLane));
            }
            catch {
                // sessionStorage may be unavailable
            }
        }
        // Cortex base URL changed — drop cached Axon/Cortex responses so they refetch on the new lane.
        void queryClient.invalidateQueries();
    }, [environment?.id, laneCount, queryClient]);
    const value = useMemo(() => ({
        selectedLane: laneCount > 1 ? selectedLaneState : 1,
        setSelectedLane,
        laneCount,
    }), [laneCount, selectedLaneState, setSelectedLane]);
    return _jsx(TimeTravelLaneContext.Provider, { value: value, children: children });
};
